// El menú "+" de la barra de entrada: adjuntar, las dos entradas que todavía
// no existen y los tres interruptores del turno.
//
// Las seis FUENTES de lo que se adjunta (archivos, producto, modelo, pose, fondo,
// imagen generada) van como CARDS en una grilla —tres por fila en la pantalla
// dedicada, dos en el panel angosto y en mobile—; las entradas "próximamente" y los
// interruptores siguen como filas debajo
// (ADR-2026-09-12-el-menu-del-mas-al-ancho-de-la-caja-con-cards).
//
// DOS CUERPOS, un contenido
// (ADR-2026-09-12-el-menu-del-mas-flota-en-desktop-y-sube-en-mobile):
// - Desktop: un popover `fixed` portaleado a `body`, del ancho de la caja de prompt y
//   apoyado sobre su borde de arriba. Va por portal porque en el arranque el composer
//   vive ADENTRO del scroller de la conversación, y un `absolute` que se pase del
//   borde superior queda recortado (el overflow negativo no se scrollea): con la caja
//   en el medio de la pantalla, el menú entero no entraba arriba. Si entre la caja y
//   el techo de la ventana no hay lugar, baja sobre la caja lo justo para entrar; si
//   ni así entra (ventana muy baja), scrollea adentro.
// - Mobile: una hoja que sube desde abajo con el fondo oscurecido, la misma anatomía
//   y animación que las hojas de Historial/Memoria/Consumo (`HojaLateral`).
//
// SE OPERA ENTERO CON EL TECLADO. Patrón `menu` de WAI-ARIA con foco rotativo:
// el disparador abre con Enter/Espacio/flecha abajo, las flechas recorren (las
// horizontales también, por la grilla), Home y End van a los extremos, Escape cierra
// y DEVUELVE el foco al "+" (sin eso el foco cae a `<body>` y hay que tabular la
// página entera para volver), y Tab cierra dejando que el foco siga su curso normal.
//
// ARRIBA, un encabezado: el título "Agregar al chat" y una X que cierra
// (ADR-2026-09-13-el-menu-del-mas-con-titulo-y-cierre). El título dice qué es esto
// —un menú de cards sin nombre se leía como una pantalla suelta— y la X es el cierre
// para el mouse y el dedo; el teclado ya tenía Escape (y Tab), que devuelven el foco al
// "+". Por eso la X queda FUERA del recorrido de las flechas y del orden de tabulación:
// no es un ítem del menú, es su cierre.
//
// El contenido y la lógica de recorrido viven en `menuMasItems.ts` (puro y
// testeado). Acá solo hay dibujo y eventos.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart3,
  Check,
  ChevronLeft,
  ChevronRight,
  Cpu,
  FileSearch,
  Globe,
  Image as ImageIcon,
  Package,
  Paperclip,
  PersonStanding,
  Plug,
  SlidersHorizontal,
  User,
  Wand2,
  Wrench,
  X,
} from 'lucide-react';
import {
  esActivable,
  idFocoSiguiente,
  idPrimerFoco,
  idUltimoFoco,
  INTERRUPTORES_MENU_MAS,
  ITEMS_MENU_MAS,
  ROTULO_PROXIMAMENTE,
  type AccionMenuMas,
  type ItemMenuMas,
  type VistaDelMenu,
} from './menuMasItems';
import { propsDeRadio } from './radiosConTeclado';
import { SelectorDeSegundos } from './SelectorDeSegundos';
import {
  OPCIONES_DE_MOTOR,
  type ProveedorDeImagen,
} from '../../lib/proveedoresDeImagen';
import type { InterruptoresChat } from '../tipos';
import {
  CALIDADES_IMAGEN,
  CALIDADES_VIDEO,
  topeDeDuracion,
  FORMATOS_IMAGEN,
  FORMATOS_VIDEO,
  TIPOS_DE_SALIDA,
  type PreferenciasDeGeneracion,
} from '../preferenciasDeGeneracion';
import { useEsEscritorio } from './useEsEscritorio';

/** Aire entre el popover y los bordes de la ventana, y entre el popover y la caja. */
const MARGEN_VENTANA = 16;
const SEPARACION_DE_LA_CAJA = 8;

interface PosicionPopover {
  left: number;
  width: number;
  /** Distancia al borde de abajo de la ventana: el menú crece hacia ARRIBA desde ahí. */
  bottom: number;
  maxHeight: number;
}

const ICONOS = {
  clip: Paperclip,
  paquete: Package,
  persona: User,
  pose: PersonStanding,
  imagen: ImageIcon,
  chispas: Wand2,
  herramientas: Wrench,
  ajustes: SlidersHorizontal,
  chip: Cpu,
  enchufe: Plug,
  'lupa-doc': FileSearch,
  globo: Globe,
  grafico: BarChart3,
} as const;

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  interruptores: InterruptoresChat;
  onCambiarInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  /** Qué acción se eligió: subir archivos o abrir uno de los selectores del catálogo
   *  (ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca). */
  onAccion: (accion: AccionMenuMas) => void;
  /**
   * Con qué motor de IA se generan las imágenes: la pantalla "Opciones de modelo
   * avanzadas" (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario). `null` = Atlas
   * elige, que es el default y lo que usa casi todo el mundo.
   *
   * Vivía en una tuerca al lado del micrófono, visible sólo para cuentas dev. La tuerca se
   * fue: un solo lugar donde se cambia el motor.
   */
  modeloDeImagen: ProveedorDeImagen | null;
  onCambiarModeloDeImagen: (proveedor: ProveedorDeImagen | null) => void;
  /** Formato y calidad por defecto para imagen y video: la pantalla "Preferencias de contenido"
   *  de adentro del menú (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas). */
  preferencias: PreferenciasDeGeneracion;
  onCambiarPreferencia: <K extends keyof PreferenciasDeGeneracion>(
    clave: K,
    valor: PreferenciasDeGeneracion[K],
  ) => void;
  /** Id del elemento disparador, para devolverle el foco al cerrar. */
  disparadorRef: React.RefObject<HTMLButtonElement>;
  /** La caja de prompt: el popover de desktop toma su ancho y se apoya en su borde de
   *  arriba. */
  anclaRef: React.RefObject<HTMLElement>;
  /** Cuántas cards por fila desde `md`: 3 en la pantalla dedicada (la caja mide
   *  ~750px), 2 en el panel lateral (~350px). Por debajo de `md` siempre son 2. */
  columnas: 2 | 3;
  /**
   * Las pantallas que NO se listan acá porque la caja ya las muestra en su fila de
   * pastillas — hoy las tres (ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas,
   * ADR-2026-09-21-todo-lo-del-menu-que-no-adjunta-sale-a-la-fila-de-pastillas).
   *
   * Es una lista y no un booleano por pantalla: lo que se saca del menú es siempre "esto
   * ya está a la vista en otro lado", y con tres banderas el llamador tenía que acordarse
   * de prender la que faltaba cada vez que una pantalla se mudaba.
   */
  vistasOcultas?: VistaDelMenu[];
}

export const MenuMas: React.FC<Props> = ({
  abierto,
  onCerrar,
  interruptores,
  onCambiarInterruptor,
  onAccion,
  modeloDeImagen,
  onCambiarModeloDeImagen,
  preferencias,
  onCambiarPreferencia,
  disparadorRef,
  anclaRef,
  columnas,
  vistasOcultas,
}) => {
  const esEscritorio = useEsEscritorio();
  const [enfocado, setEnfocado] = useState<string | null>(null);
  // Qué se ve adentro del menú: la lista, o la pantalla de preferencias. Se vuelve a la
  // lista al cerrar, así la próxima apertura arranca donde siempre.
  const [vista, setVista] = useState<'principal' | VistaDelMenu>('principal');
  const [pos, setPos] = useState<PosicionPopover | null>(null);
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const opcionesRef = useRef<Record<string, HTMLButtonElement | null>>({});
  const volverRef = useRef<HTMLButtonElement | null>(null);

  // Las entradas de ESTE menú. Con las pastillas en la caja no está ninguna de las tres
  // pantallas — y el foco rotativo tiene que recorrer la lista SIN ellas: si se recorriera
  // la del módulo, las flechas pararían en entradas que no se dibujan.
  //
  // `join` en las dependencias y no el array: el llamador puede pasar uno nuevo en cada
  // render y el `useMemo` no serviría de nada.
  const ocultas = vistasOcultas?.join(',') ?? '';
  const items = useMemo(
    () =>
      ocultas === ''
        ? ITEMS_MENU_MAS
        : ITEMS_MENU_MAS.filter(
            (i) => !(i.tipo === 'vista' && ocultas.split(',').includes(i.id)),
          ),
    [ocultas],
  );

  // Al abrir, el foco arranca en el primer ítem. `useLayoutEffect` para que
  // pase en el mismo frame en que el menú aparece: con `useEffect` se veía un
  // parpadeo del anillo de foco saltando desde el "+".
  useLayoutEffect(() => {
    if (!abierto) {
      setEnfocado(null);
      setVista('principal');
      return;
    }
    setEnfocado(idPrimerFoco(items));
  }, [abierto]);

  // Mover el foco de verdad al ítem marcado (el DOM y el estado tienen que ir
  // juntos: `aria-activedescendant` no alcanza acá porque los ítems son
  // botones reales, no filas de una lista virtual). `pos` va en las dependencias
  // a propósito: en desktop el popover se monta con `visibility: hidden` hasta que
  // se lo mide, y un elemento oculto NO toma el foco; cuando `pos` llega, se vuelve
  // a intentar sobre el menú ya visible.
  //
  // Adentro de una pantalla el foco va a "Volver": el ítem que lo tenía se desmontó junto
  // con la lista y, sin esto, el foco se caía al `body` — quien navega con teclado perdía
  // el lugar y no le quedaba ni cómo volver. `vista` está en las dependencias para que al
  // volver a la lista el foco regrese aunque `enfocado` no haya cambiado.
  useEffect(() => {
    if (!abierto) return;
    if (vista !== 'principal') {
      volverRef.current?.focus();
      return;
    }
    if (enfocado) opcionesRef.current[enfocado]?.focus();
  }, [abierto, enfocado, pos, vista]);

  // Cerrar al tocar afuera. `mousedown` y no `click`: si esperáramos al click,
  // un `mousedown` sobre el "+" ya habría disparado su propio toggle y el menú
  // se cerraría y volvería a abrir.
  useEffect(() => {
    if (!abierto) return;
    const afuera = (ev: MouseEvent) => {
      const destino = ev.target as Node;
      if (contenedorRef.current?.contains(destino)) return;
      if (disparadorRef.current?.contains(destino)) return;
      onCerrar();
    };
    document.addEventListener('mousedown', afuera);
    return () => document.removeEventListener('mousedown', afuera);
  }, [abierto, onCerrar, disparadorRef]);

  // Dónde va el popover de desktop. Se calcula en un layout effect, ANTES del primer
  // pintado: el menú se monta oculto, se le fija el ancho de la caja (el alto depende
  // del ancho, por las cards que envuelven), se mide, y con eso se decide desde dónde
  // crece. Preferido: apoyado sobre la caja. Si así se pasaría del techo, baja sobre
  // la caja lo justo; nunca más abajo que el margen. Con el ancho ya puesto a mano,
  // el render con `pos` deja el mismo número.
  useLayoutEffect(() => {
    if (!abierto || !esEscritorio) {
      setPos(null);
      return;
    }
    const ancla = anclaRef.current;
    const el = contenedorRef.current;
    if (!ancla || !el) return;
    const rect = ancla.getBoundingClientRect();
    const altoVentana = window.innerHeight;
    const maxHeight = altoVentana - MARGEN_VENTANA * 2;
    el.style.width = `${rect.width}px`;
    el.style.maxHeight = `${maxHeight}px`;
    const alto = el.offsetHeight;
    const preferido = altoVentana - rect.top + SEPARACION_DE_LA_CAJA;
    const tope = altoVentana - MARGEN_VENTANA - alto;
    setPos({
      left: rect.left,
      width: rect.width,
      bottom: Math.max(MARGEN_VENTANA, Math.min(preferido, tope)),
      maxHeight,
    });
  }, [abierto, esEscritorio, anclaRef]);

  // Un `fixed` no acompaña a su ancla: si la ventana cambia de tamaño o algo de atrás
  // scrollea, el menú se cierra antes de quedar flotando en cualquier lado. El scroll
  // ADENTRO del menú no cuenta.
  useEffect(() => {
    if (!abierto || !esEscritorio) return;
    const alScroll = (ev: Event) => {
      if (contenedorRef.current?.contains(ev.target as Node)) return;
      onCerrar();
    };
    window.addEventListener('resize', onCerrar);
    document.addEventListener('scroll', alScroll, true);
    return () => {
      window.removeEventListener('resize', onCerrar);
      document.removeEventListener('scroll', alScroll, true);
    };
  }, [abierto, esEscritorio, onCerrar]);

  if (!abierto) return null;

  const cerrarYDevolverFoco = () => {
    onCerrar();
    disparadorRef.current?.focus();
  };

  const activar = (item: ItemMenuMas) => {
    if (item.tipo === 'accion') {
      onAccion(item.id);
      cerrarYDevolverFoco();
      return;
    }
    if (item.tipo === 'vista') {
      // Abre la pantalla adentro del menú, sin cerrarlo. El id del ítem ES la pantalla.
      setVista(item.id);
      return;
    }
    if (item.tipo === 'toggle') {
      // El menú NO se cierra al tocar un interruptor: casi siempre se prende
      // más de uno de una sentada.
      onCambiarInterruptor(item.id, !interruptores[item.id]);
    }
  };

  const alTeclado = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      // Adentro de una pantalla, Escape vuelve a la lista; recién ahí cierra.
      if (vista !== 'principal') {
        setVista('principal');
        setEnfocado(idPrimerFoco(items));
        return;
      }
      cerrarYDevolverFoco();
      return;
    }
    if (ev.key === 'Tab') {
      // Adentro de una pantalla Tab recorre sus controles; en la lista, cierra.
      if (vista !== 'principal') return;
      onCerrar();
      return;
    }
    // Las flechas son el foco rotativo de la LISTA. Adentro de una pantalla las manejan
    // los `radiogroup` (`propsDeRadio`), que además cortan la propagación.
    if (vista !== 'principal') return;
    // Las flechas horizontales recorren igual que las verticales: en la grilla de
    // cards es lo que uno espera, y en las filas de abajo no molesta.
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowRight' || ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') {
      ev.preventDefault();
      const adelante = ev.key === 'ArrowDown' || ev.key === 'ArrowRight';
      setEnfocado(idFocoSiguiente(enfocado, adelante ? 1 : -1, items));
      return;
    }
    if (ev.key === 'Home') {
      ev.preventDefault();
      setEnfocado(idPrimerFoco(items));
      return;
    }
    if (ev.key === 'End') {
      ev.preventDefault();
      setEnfocado(idUltimoFoco(items));
    }
  };

  // Las fuentes van juntas en la grilla y el resto (las pantallas, "próximamente")
  // sigue como filas. El ORDEN del foco es el de `ITEMS_MENU_MAS`, que
  // ya pone las seis acciones primero: la grilla y las filas se recorren seguidas.
  const fuentes = items.filter((i) => i.tipo === 'accion');
  const filas = items.filter((i) => i.tipo !== 'accion');

  const lista = (
    <>
      {/* El encabezado: título a la izquierda, la X en la esquina. La X no es
          `menuitem` ni entra en el Tab (`tabIndex=-1`): el teclado cierra con Escape. */}
      <div className="flex items-center justify-between gap-2 pl-2 pr-0.5 pb-2 pt-0.5">
        <p className="min-w-0 truncate text-[14px] font-semibold text-brand-black">Agregar al chat</p>
        <button
          type="button"
          tabIndex={-1}
          onClick={cerrarYDevolverFoco}
          aria-label="Cerrar"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.55] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black"
        >
          <X className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden="true" />
        </button>
      </div>
      <div
        role="group"
        aria-label="Adjuntar"
        className={`grid grid-cols-2 gap-2 ${columnas === 3 ? 'md:grid-cols-3' : ''}`}
      >
        {fuentes.map((item) => {
          if (item.tipo !== 'accion') return null;
          const Icono = ICONOS[item.icono];
          return (
            <button
              key={item.id}
              type="button"
              ref={(el) => {
                opcionesRef.current[item.id] = el;
              }}
              role="menuitem"
              tabIndex={enfocado === item.id ? 0 : -1}
              onFocus={() => setEnfocado(item.id)}
              onClick={() => activar(item)}
              // La card: relleno suave (no un borde fino sobre blanco, que se leía como
              // una tabla), el ícono en una ficha blanca arriba y el texto abajo, a la
              // izquierda. El hover toma el azul clarito de las sugerencias.
              className="flex flex-col items-start gap-2.5 rounded-lg bg-[#F4F6FB] p-3 text-left transition-colors hover:bg-[#E9EDFD] focus-visible:bg-[#E9EDFD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              <span
                aria-hidden="true"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-brand-primary shadow-sm"
              >
                <Icono className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold leading-snug text-brand-black">
                  {item.etiqueta}
                </span>
                <span className="mt-0.5 block text-[12px] leading-snug text-[#02062B]/[0.5]">
                  {item.ayuda}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Las filas, con un poco de aire tras la grilla: antes lo ponía el separador que
          ya no está. */}
      <div className="mt-1">
        {filas.map((item) => {
          if (item.tipo === 'separador') {
            return (
              <div
                key={item.id}
                role="separator"
                className="my-1 h-px bg-[#02062B]/[0.07]"
              />
            );
          }
          return filaDeItem(item, true);
        })}
      </div>
    </>
  );

  /** Una fila del menú. `enLista`: en la lista principal entra al foco rotativo (ref,
   *  `tabIndex` según el marcado); en una pantalla de adentro es un control común. */
  function filaDeItem(item: Exclude<ItemMenuMas, { tipo: 'separador' }>, enLista: boolean) {
          const Icono = ICONOS[item.icono];
          const activo = item.tipo === 'toggle' ? interruptores[item.id] === true : false;
          const deshabilitado = item.tipo === 'proximamente';

          return (
            <button
              key={item.id}
              type="button"
              ref={
                enLista
                  ? (el) => {
                      opcionesRef.current[item.id] = el;
                    }
                  : undefined
              }
              role={item.tipo === 'toggle' ? 'menuitemcheckbox' : 'menuitem'}
              {...(item.tipo === 'toggle' ? { 'aria-checked': activo } : {})}
              aria-disabled={deshabilitado || undefined}
              // Foco rotativo: solo el ítem marcado entra en el orden de tabulación.
              tabIndex={enLista ? (enfocado === item.id ? 0 : -1) : 0}
              onFocus={enLista ? () => setEnfocado(item.id) : undefined}
              onClick={() => esActivable(item) && activar(item)}
              className={`flex w-full items-center gap-2.5 rounded-lg py-2 px-3 text-left text-[14px] transition-colors ${
                deshabilitado
                  ? // Apagada, NO rota: gris parejo, sin hover, sin rojo, sin
                    // tachado. Lo que la explica es el rótulo "próximamente" que
                    // va a la derecha, no el color.
                    'cursor-default text-[#02062B]/[0.38]'
                  : 'text-brand-black hover:bg-[#02062B]/[0.04] focus-visible:bg-[#02062B]/[0.04]'
              }`}
            >
              <Icono
                className={`h-[18px] w-[18px] shrink-0 ${
                  deshabilitado
                    ? 'text-[#02062B]/[0.3]'
                    : activo
                      ? 'text-brand-primary'
                      : 'text-[#02062B]/[0.55]'
                }`}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className={`block truncate ${activo ? 'font-semibold' : 'font-medium'}`}>
                  {item.etiqueta}
                </span>
                {/* La ayuda es lo que distingue "Investigación" de "Búsqueda web"
                    para alguien que las ve por primera vez. Va siempre, no solo
                    en hover: un tooltip no existe para el teclado ni para touch. */}
                <span
                  className={`block truncate text-[12px] ${
                    deshabilitado ? 'text-[#02062B]/[0.32]' : 'text-[#02062B]/[0.5]'
                  }`}
                >
                  {item.ayuda}
                </span>
              </span>

              {item.tipo === 'proximamente' && (
                <span className="shrink-0 rounded-full bg-[#02062B]/[0.05] py-0.5 px-2 text-[11px] font-medium text-[#02062B]/[0.45]">
                  {ROTULO_PROXIMAMENTE}
                </span>
              )}

              {item.tipo === 'vista' && (
                // La flecha dice "acá adentro hay más": abre una pantalla, no prende nada.
                <ChevronRight className="h-4 w-4 shrink-0 text-[#02062B]/[0.35]" aria-hidden="true" />
              )}

              {item.tipo === 'toggle' && (
                // La marca de activo no es solo color: es una casilla que se
                // llena y una tilde. Y `aria-checked` lo dice aparte.
                <span
                  aria-hidden="true"
                  className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border transition-colors ${
                    activo
                      ? 'border-brand-primary bg-brand-primary text-white'
                      : 'border-[#02062B]/[0.18] bg-white'
                  }`}
                >
                  {activo && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
              )}
            </button>
          );
  }

  // ── La pantalla de preferencias: las filas de pastillas y la aclaración ────
  // "Automático" = sin preferencia (null): el agente decide como siempre. Cada fila es un
  // `radiogroup`; las pastillas son `radio` con su `aria-checked`.
  //
  // `conAutomatico` existe por el TIPO DE CONTENIDO: es el switch de la caja y no tiene
  // "sin preferencia" —siempre está en una de las dos posiciones
  // (ADR-2026-09-21-el-switch-de-imagen-o-video)—, así que esa fila se dibuja sin la
  // pastilla de Automático y `elegir` nunca recibe null.
  const filaDeOpciones = <V extends string | number>(
    rotulo: string,
    ayuda: string,
    opciones: { id: V; etiqueta: string; detalle?: string }[],
    valor: V | null,
    elegir: (v: V | null) => void,
    conAutomatico = true,
  ) => (
    <div className="px-2 pt-3" role="radiogroup" aria-label={rotulo}>
      <p className="text-[12.5px] font-semibold text-brand-black">{rotulo}</p>
      <p className="mb-2 text-[11.5px] text-[#02062B]/[0.5]">{ayuda}</p>
      <div className="flex flex-wrap gap-1.5">
        {(() => {
          const todas = [
            ...(conAutomatico
              ? [{ id: null as V | null, etiqueta: 'Automático', detalle: undefined as string | undefined }]
              : []),
            ...opciones,
          ];
          const ids = todas.map((o) => o.id);
          return todas.map((o) => {
          const activo = o.id === valor;
          return (
            <button
              key={o.id ?? 'auto'}
              type="button"
              role="radio"
              aria-checked={activo}
              {...propsDeRadio(ids, valor, o.id, elegir)}
              onClick={() => elegir(o.id)}
              title={o.detalle}
              className={`rounded-full py-1.5 px-3 text-[12.5px] font-semibold transition-colors ${
                activo
                  ? 'bg-[#E9EDFD] text-brand-primary ring-1 ring-brand-primary'
                  : 'bg-neutral-100 text-[#02062BE6] hover:bg-neutral-200'
              }`}
            >
              {o.etiqueta}
              {o.detalle && (
                <span className={`ml-1 font-normal ${activo ? 'text-brand-primary' : 'text-[#02062B]/[0.45]'}`}>
                  · {o.detalle}
                </span>
              )}
            </button>
          );
          });
        })()}
      </div>
    </div>
  );

  // "Volver" + título. Lo comparten las TRES pantallas de adentro del menú: si se
  // dibujaran aparte, el día que se corrija el padding de una la otra quedaría distinta.
  const cabeceraDeVista = (titulo: string) => (
    <div className="flex items-center gap-2 px-1 pt-1">
      <button
        type="button"
        ref={volverRef}
        onClick={() => {
          setVista('principal');
          setEnfocado(idPrimerFoco(items));
        }}
        className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[#E9EDFD]"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
        Volver
      </button>
      <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-brand-black">
        {titulo}
      </p>
    </div>
  );

  /* ── Herramientas: los tres interruptores del chat, en su pantalla ──────────
     ADR-2026-09-18-herramientas-es-una-pantalla-del-menu-del-mas. Las mismas filas
     (`filaDeItem`) que tenía la lista, ahora fuera del foco rotativo: acá Tab recorre
     los controles (como en las otras pantallas) y las flechas van de un interruptor al
     otro sin salir del grupo. Se prenden y apagan sin cerrar nada. */
  const alTecladoEnHerramientas = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
    const botones = Array.from(ev.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
    const n = botones.indexOf(document.activeElement as HTMLButtonElement);
    if (n === -1) return;
    ev.preventDefault();
    ev.stopPropagation();
    const delta = ev.key === 'ArrowDown' ? 1 : -1;
    botones[(n + delta + botones.length) % botones.length]?.focus();
  };
  const pantallaDeHerramientas = (
    <div>
      {cabeceraDeVista('Herramientas')}
      <p className="mx-2 mt-2 rounded-lg bg-[#F4F6FB] px-3 py-2 text-[12px] leading-snug text-[#02062B]/[0.7]">
        Lo que el agente puede usar al responder. Quedan como las dejes.
      </p>
      <div role="group" aria-label="Herramientas" className="mt-1 pb-1" onKeyDown={alTecladoEnHerramientas}>
        {INTERRUPTORES_MENU_MAS.map((item) => filaDeItem(item, false))}
      </div>
    </div>
  );

  const pantallaDePreferencias = (
    <div>
      {cabeceraDeVista('Preferencias de contenido')}
      {/* La aclaración va ARRIBA: es lo que le da sentido a todo lo de abajo. */}
      <p className="mx-2 mt-2 rounded-lg bg-[#F4F6FB] px-3 py-2 text-[12px] leading-snug text-[#02062B]/[0.7]">
        Son las opciones que el agente usa <strong className="font-semibold">por defecto</strong> al
        generar. Si en un mensaje le pedís otra cosa, gana lo que le pedís.
      </p>
      {/* Sin subtítulos "Imagen" / "Video": cada fila ya dice de qué es en su rótulo,
          y con cuatro filas los subtítulos eran una jerarquía de más
          (ADR-2026-09-13-el-menu-del-mas-con-titulo-y-cierre). */}
      {/* El tipo de contenido PRIMERO: es el switch de la caja y lo que decide cuál de las
          dos mitades de abajo se usa (ADR-2026-09-21-el-switch-de-imagen-o-video). Sin
          "Automático": el switch siempre está en una posición. */}
      {filaDeOpciones(
        'Tipo de contenido',
        'Qué estás por hacer. El agente lo asume desde el primer mensaje, salvo que le pidas lo otro.',
        TIPOS_DE_SALIDA,
        preferencias.tipoDeSalida,
        (v) => v && onCambiarPreferencia('tipoDeSalida', v),
        false,
      )}
      {filaDeOpciones(
        'Formato de imagen',
        'La proporción de la imagen.',
        FORMATOS_IMAGEN,
        preferencias.formatoImagen,
        (v) => onCambiarPreferencia('formatoImagen', v),
      )}
      {filaDeOpciones(
        'Calidad de imagen',
        'La resolución de salida; cambia lo que cuesta cada imagen.',
        CALIDADES_IMAGEN,
        preferencias.calidadImagen,
        (v) => onCambiarPreferencia('calidadImagen', v),
      )}
      {filaDeOpciones(
        'Formato de video',
        'La proporción del video.',
        FORMATOS_VIDEO,
        preferencias.formatoVideo,
        (v) => onCambiarPreferencia('formatoVideo', v),
      )}
      {filaDeOpciones(
        'Calidad de video',
        'Estándar es más barata y llega a 15 segundos; Profesional usa el mejor modelo y llega a 30.',
        CALIDADES_VIDEO,
        preferencias.calidadVideo,
        (v) => onCambiarPreferencia('calidadVideo', v),
      )}
      {/* La duración NO es una fila de pastillas: es "Automático" + un slider al segundo
          (ADR-2026-09-21-la-duracion-se-elige-al-segundo). El mismo control que la pastilla
          de la caja y que el sheet de la card de video, porque es la misma duración: con
          cuatro saltos, un 8 puesto desde cualquiera de los otros dos lugares no coincidía
          con ninguna pastilla y la fila se veía sin nada elegido. El tope lo baja la
          calidad Estándar. */}
      <div className="px-2 pt-3" role="group" aria-label="Duración del video">
        <p className="text-[12.5px] font-semibold text-brand-black">Duración del video</p>
        <p className="mb-2 text-[11.5px] text-[#02062B]/[0.5]">
          Cuánto dura. Se cobra por segundo, así que el doble de segundos cuesta el doble.
        </p>
        <SelectorDeSegundos
          id="menu-mas-duracion-video"
          valor={preferencias.duracionVideo}
          tope={topeDeDuracion(preferencias.calidadVideo)}
          onCambiar={(segundos) => onCambiarPreferencia('duracionVideo', segundos)}
        />
      </div>
      <div className="h-2" />
    </div>
  );

  /* ── Opciones de modelo avanzadas ──────────────────────────────────────────
     ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario.

     Filas y no pastillas como en Preferencias: cada motor necesita una línea que diga para
     qué es bueno —los nombres solos ("Seedream 5.0 pro") no le dicen nada a nadie— y seis
     pastillas con subtítulo no entran en el ancho del panel.

     Un `radiogroup`: es una elección entre opciones excluyentes, igual que las filas de
     Preferencias, y el teclado tiene que poder recorrerlas. */
  const pantallaDeModelos = (
    <div>
      {cabeceraDeVista('Opciones de modelo avanzadas')}
      {/* Sin aclaración arriba, al revés que Preferencias: allá el cartel explica QUÉ son
          esas opciones (algo que no se deduce de las pastillas), acá cada fila ya trae su
          propia línea y el default se llama Atlas, que se explica solo. */}
      <div role="radiogroup" aria-label="Motor de imagen" className="mt-2 px-1 pb-2">
        {OPCIONES_DE_MOTOR.map((opt) => {
          const activo = opt.id === modeloDeImagen;
          return (
            <button
              key={opt.id ?? 'atlas'}
              type="button"
              role="radio"
              aria-checked={activo}
              {...propsDeRadio(
                OPCIONES_DE_MOTOR.map((o) => o.id),
                modeloDeImagen,
                opt.id,
                onCambiarModeloDeImagen,
              )}
              onClick={() => onCambiarModeloDeImagen(opt.id)}
              className={`flex w-full items-start gap-3 rounded-xl py-2.5 px-3 text-left transition-colors ${
                activo ? 'bg-[#E9EDFD]' : 'hover:bg-[#02062B]/[0.04]'
              }`}
            >
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-[14px] ${
                    activo ? 'font-semibold text-brand-primary' : 'font-medium text-brand-black'
                  }`}
                >
                  {opt.label}
                </span>
                <span className="mt-0.5 block text-[12px] leading-snug text-[#02062B]/[0.5]">
                  {opt.detalle}
                </span>
              </span>
              {/* Como en los toggles de la lista: la marca no es sólo color. */}
              <span
                aria-hidden="true"
                className={`mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${
                  activo
                    ? 'border-brand-primary bg-brand-primary text-white'
                    : 'border-[#02062B]/[0.18] bg-white'
                }`}
              >
                {activo && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const contenido =
    vista === 'herramientas'
      ? pantallaDeHerramientas
      : vista === 'preferencias'
        ? pantallaDePreferencias
        : vista === 'modelos'
          ? pantallaDeModelos
          : lista;

  if (!esEscritorio) {
    // La hoja: fondo oscuro que cierra al tocarlo y el panel que sube desde abajo.
    // Radios concéntricos: hoja 20 = card/fila 8 + 12 de padding lateral.
    return createPortal(
      <>
        <div className="fixed inset-0 z-[130] bg-black/40 animate-fade-in" onClick={onCerrar} />
        <div
          ref={contenedorRef}
          role="menu"
          aria-label="Más opciones"
          onKeyDown={alTeclado}
          className="fixed inset-x-0 bottom-0 z-[131] flex flex-col rounded-t-[20px] bg-white shadow-2xl animate-slide-up-sheet"
          style={{
            maxHeight: '85dvh',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          }}
        >
          <div className="shrink-0 py-2">
            <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.15]" />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2">{contenido}</div>
        </div>
      </>,
      document.body,
    );
  }

  // El popover: `fixed`, del ancho de la caja, creciendo hacia arriba desde `bottom`.
  // Hasta que el layout effect lo mide va oculto (un frame que no se pinta). Radios
  // concéntricos: contenedor 16 = card/fila 8 + 8 de padding.
  return createPortal(
    <div
      ref={contenedorRef}
      role="menu"
      aria-label="Más opciones"
      onKeyDown={alTeclado}
      className="fixed z-[130] overflow-y-auto overscroll-contain rounded-2xl border border-[#02062B]/[0.08] bg-white p-2 shadow-[0_12px_32px_rgba(2,6,43,0.16)] animate-mode-swap"
      style={
        pos
          ? { left: pos.left, width: pos.width, bottom: pos.bottom, maxHeight: pos.maxHeight }
          : { left: 0, bottom: 0, visibility: 'hidden' }
      }
    >
      {contenido}
    </div>,
    document.body,
  );
};

export default MenuMas;
